import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type { PurchaseOrder } from "serviceStores/mock/purchase-orders.mock";
import type { ApprovalQueueTask } from "serviceStores/mock/approval-queue.mock";

/**
 * Payment method types
 */
type PaymentMethod = "check" | "ach" | "wire" | "credit_card";

/**
 * Form data for PO payment
 */
interface POPaymentFormData {
  // Selected task/PO
  taskId: number | null;
  task: ApprovalQueueTask | null;
  purchaseOrder: PurchaseOrder | null;

  // Step 1: Payment Method
  paymentMethod: PaymentMethod | null;

  // Step 2: Payment Details
  checkNumber: string;
  achReference: string;
  wireReference: string;
  creditCardLast4: string;
  paymentDate: string;
  paymentAmount: number;
  paymentNotes: string;

  // Bank account selection (for ACH)
  bankAccountId: number | null;
}

/**
 * Initial form data
 */
const initialFormData: POPaymentFormData = {
  taskId: null,
  task: null,
  purchaseOrder: null,
  paymentMethod: null,
  checkNumber: "",
  achReference: "",
  wireReference: "",
  creditCardLast4: "",
  paymentDate: new Date().toISOString().split("T")[0],
  paymentAmount: 0,
  paymentNotes: "",
  bankAccountId: null,
};

/**
 * Bank account for ACH payments
 */
interface BankAccount {
  id: number;
  name: string;
  accountLast4: string;
  routingLast4: string;
  bankName: string;
}

/**
 * PO Payment Wizard Store
 *
 * Manages state for the Purchase Order payment wizard.
 * Used by accounts payable to process payments for approved POs.
 */
class POPaymentWizardStore {
  rootStore: RootStore;

  // Step navigation
  activeStep = 0;
  readonly steps = ["Select Method", "Payment Details", "Confirm Payment"];

  // UI State
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: POPaymentFormData = { ...initialFormData };

  // Reference Data
  pendingTasks: ApprovalQueueTask[] = [];
  bankAccounts: BankAccount[] = [
    {
      id: 1,
      name: "Operating Account",
      accountLast4: "4521",
      routingLast4: "0234",
      bankName: "First National Bank",
    },
    {
      id: 2,
      name: "Payroll Account",
      accountLast4: "7832",
      routingLast4: "0234",
      bankName: "First National Bank",
    },
    {
      id: 3,
      name: "Materials Account",
      accountLast4: "9156",
      routingLast4: "5567",
      bankName: "Regional Credit Union",
    },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Step Navigation ====================

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  canProceed = (): boolean => {
    return this.validateCurrentStep(false);
  };

  // ==================== Task Selection ====================

  selectTask = async (taskId: number) => {
    const task = this.pendingTasks.find((t) => t.taskId === taskId);
    if (!task) return;

    this.loading = true;
    this.formData.taskId = taskId;
    this.formData.task = task;

    try {
      // Load the purchase order
      const purchaseOrdersStore = (this.rootStore as any).purchaseOrdersStore;
      if (purchaseOrdersStore) {
        const pos = purchaseOrdersStore.entities.filter(
          (po: PurchaseOrder) => po.purchaseOrderId === task.entityId
        );
        if (pos.length > 0) {
          runInAction(() => {
            this.formData.purchaseOrder = pos[0];
            this.formData.paymentAmount = pos[0].totalAmount;
          });
        }
      }

      runInAction(() => {
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load PO details";
        this.loading = false;
      });
    }
  };

  clearSelectedTask = () => {
    this.formData = { ...initialFormData };
    this.activeStep = 0;
  };

  // ==================== Payment Method ====================

  setPaymentMethod = (method: PaymentMethod) => {
    this.formData.paymentMethod = method;
    // Clear other method-specific fields
    this.formData.checkNumber = "";
    this.formData.achReference = "";
    this.formData.wireReference = "";
    this.formData.creditCardLast4 = "";
    this.formData.bankAccountId = null;
  };

  // ==================== Form Updates ====================

  updateCheckNumber = (value: string) => {
    this.formData.checkNumber = value;
  };

  updateACHReference = (value: string) => {
    this.formData.achReference = value;
  };

  updateWireReference = (value: string) => {
    this.formData.wireReference = value;
  };

  updateCreditCardLast4 = (value: string) => {
    this.formData.creditCardLast4 = value.replace(/\D/g, "").slice(0, 4);
  };

  updatePaymentDate = (value: string) => {
    this.formData.paymentDate = value;
  };

  updatePaymentAmount = (value: number) => {
    this.formData.paymentAmount = value;
  };

  updatePaymentNotes = (value: string) => {
    this.formData.paymentNotes = value;
  };

  setBankAccount = (accountId: number) => {
    this.formData.bankAccountId = accountId;
  };

  // ==================== Computed Values ====================

  get paymentMethodLabel(): string {
    switch (this.formData.paymentMethod) {
      case "check":
        return "Check";
      case "ach":
        return "ACH Transfer";
      case "wire":
        return "Wire Transfer";
      case "credit_card":
        return "Credit Card";
      default:
        return "Not Selected";
    }
  }

  get paymentReference(): string {
    switch (this.formData.paymentMethod) {
      case "check":
        return `Check #${this.formData.checkNumber}`;
      case "ach":
        return `ACH: ${this.formData.achReference}`;
      case "wire":
        return `Wire: ${this.formData.wireReference}`;
      case "credit_card":
        return `Card ending in ${this.formData.creditCardLast4}`;
      default:
        return "";
    }
  }

  get selectedBankAccount(): BankAccount | undefined {
    return this.bankAccounts.find((a) => a.id === this.formData.bankAccountId);
  }

  get isPartialPayment(): boolean {
    const poAmount = this.formData.purchaseOrder?.totalAmount || 0;
    return (
      this.formData.paymentAmount > 0 &&
      Math.abs(this.formData.paymentAmount - poAmount) > 0.01
    );
  }

  // ==================== Validation ====================

  validateCurrentStep = (showError = true): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0: // Payment Method
        if (!this.formData.purchaseOrder) {
          if (showError)
            this.error = "Please select a Purchase Order to pay";
          return false;
        }
        if (!this.formData.paymentMethod) {
          if (showError) this.error = "Please select a payment method";
          return false;
        }
        return true;

      case 1: // Payment Details
        if (!this.formData.paymentDate) {
          if (showError) this.error = "Please enter the payment date";
          return false;
        }
        if (this.formData.paymentAmount <= 0) {
          if (showError) this.error = "Please enter a valid payment amount";
          return false;
        }

        switch (this.formData.paymentMethod) {
          case "check":
            if (!this.formData.checkNumber) {
              if (showError) this.error = "Please enter the check number";
              return false;
            }
            break;
          case "ach":
            if (!this.formData.bankAccountId) {
              if (showError) this.error = "Please select a bank account";
              return false;
            }
            break;
          case "wire":
            if (!this.formData.wireReference) {
              if (showError) this.error = "Please enter the wire reference";
              return false;
            }
            break;
          case "credit_card":
            if (this.formData.creditCardLast4.length !== 4) {
              if (showError)
                this.error = "Please enter the last 4 digits of the card";
              return false;
            }
            break;
        }
        return true;

      case 2: // Confirm
        return true;

      default:
        return true;
    }
  };

  // ==================== Data Loading ====================

  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load pending payment tasks
      if ((this.rootStore as any).approvalQueueStore) {
        await (this.rootStore as any).approvalQueueStore.find({
          query: {
            queueType: "payment",
            status: "pending",
          },
        });
      }

      // Load POs for context
      if ((this.rootStore as any).purchaseOrdersStore) {
        await (this.rootStore as any).purchaseOrdersStore.find({
          query: { status: "approved" },
        });
      }

      runInAction(() => {
        const allTasks =
          (this.rootStore as any).approvalQueueStore?.entities || [];
        this.pendingTasks = allTasks.filter(
          (t: ApprovalQueueTask) =>
            t.queueType === "payment" && t.status === "pending"
        );
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  // ==================== Submission ====================

  generatePaymentNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `PAY-${year}${month}${day}-${random}`;
  };

  submitPayment = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const po = this.formData.purchaseOrder!;
      const task = this.formData.task!;
      const paymentNumber = this.generatePaymentNumber();

      // Update PO status
      const purchaseOrdersStore = (this.rootStore as any).purchaseOrdersStore;
      if (purchaseOrdersStore) {
        const isFullPayment = !this.isPartialPayment;
        await purchaseOrdersStore.patch(po.purchaseOrderId, {
          status: isFullPayment ? "paid" : "partial_paid",
          paymentMethod: this.formData.paymentMethod,
          paymentReference: this.paymentReference,
          paymentDate: this.formData.paymentDate,
          paidAmount: (po.paidAmount || 0) + this.formData.paymentAmount,
        });
      }

      // Update payment queue task
      const approvalQueueStore = (this.rootStore as any).approvalQueueStore;
      if (approvalQueueStore) {
        await approvalQueueStore.patch(task.taskId, {
          status: "completed",
          completedDate: new Date().toISOString().split("T")[0],
          completedBy: (this.rootStore as any).authStore?.user?.id || 1,
          completedByName:
            (this.rootStore as any).authStore?.user?.name || "Current User",
          resolution: "paid",
          notes: `${this.paymentMethodLabel}: ${this.paymentReference}`,
          paymentNumber: paymentNumber,
        });
      }

      runInAction(() => {
        this.success = `Payment ${paymentNumber} processed successfully! ${this.paymentMethodLabel} for ${this.formData.paymentAmount.toLocaleString(
          "en-US",
          { style: "currency", currency: "USD" }
        )}`;
        this.loading = false;

        // Navigate to payment queue after delay
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("PaymentQueue");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to process payment";
        this.loading = false;
      });
    }
  };

  // ==================== Reset ====================

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = { ...initialFormData };
  };
}

export default POPaymentWizardStore;
